/**
 * Notice: Some code was adapted from Material-UI's text field.
 *         Copyright (c) 2014 Call-Em-All (https://github.com/callemall/material-ui)
 */
import React, { ReactElement } from 'react';
import ReactDOM from 'react-dom';
import Input, { InputProps as StandardInputProps } from '@mui/material/Input';
import FilledInput from '@mui/material/FilledInput/FilledInput';
import OutlinedInput, { OutlinedInputProps } from '@mui/material/OutlinedInput';
import InputLabel, { InputLabelProps } from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import blue from '@mui/material/colors/blue';
import FormControl, { FormControlProps } from '@mui/material/FormControl';
import FormHelperText, { FormHelperTextProps } from '@mui/material/FormHelperText';
import cx from 'classnames';
import { FilledInputProps } from '@mui/material/FilledInput';

export interface ChipRendererArgs {
  value: string;
  text: string;
  chip: any;
  isFocused: boolean;
  isDisabled: boolean;
  isReadOnly: boolean;
  handleClick: React.EventHandler<any>;
  handleDelete: React.EventHandler<any>;
  className: string;
}

export type ChipRenderer = (args: ChipRendererArgs, key: any) => React.ReactNode;

type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;

// omitting onChange from FormControlProps as we use a custom onChange
export interface BaseTextFieldProps extends Omit<FormControlProps, 'onChange'> {
  allowDuplicates?: boolean;
  alwaysShowPlaceholder?: boolean;
  blurBehavior?: 'clear' | 'add' | 'add-or-clear' | 'ignore';
  chipRenderer?: ChipRenderer;
  classes?: Record<string, string>;
  clearInputValueOnChange?: boolean;
  dataSource?: any[];
  dataSourceConfig?: {
    text: string;
    value: string;
  };
  defaultValue?: any[];
  delayBeforeAdd?: false;
  disabled?: boolean;
  disableUnderline?: boolean;
  FormHelperTextProps?: FormHelperTextProps;
  fullWidth?: boolean;
  fullWidthInput?: boolean;
  helperText?: React.ReactNode;
  InputLabelProps?: InputLabelProps;
  inputRef?: (ref: React.Ref<HTMLInputElement>) => any;
  inputValue?: string;
  label?: React.ReactNode;
  newChipKeyCodes?: number[];
  newChipKeys?: string[];
  onAdd?: (chip: any) => any;
  onBeforeAdd?: (chip: any) => boolean;
  onChange?: (chips: any[]) => any;
  onDelete?: (chip: any, index: number) => any;
  onUpdateInput?: React.EventHandler<any>;
  placeholder?: string;
  readOnly?: boolean;
  value?: any[];
  variant?: 'outlined' | 'standard' | 'filled';
}

export interface StandardTextFieldProps extends BaseTextFieldProps {
  variant?: 'standard';
  InputProps?: Partial<StandardInputProps>;
}

export interface FilledTextFieldProps extends BaseTextFieldProps {
  variant: 'filled';
  InputProps?: Partial<FilledInputProps>;
}

export interface OutlinedTextFieldProps extends BaseTextFieldProps {
  variant: 'outlined';
  InputProps?: Partial<OutlinedInputProps>;
}

export type Props = StandardTextFieldProps | FilledTextFieldProps | OutlinedTextFieldProps;

const variantComponent = {
  standard: Input,
  filled: FilledInput,
  outlined: OutlinedInput,
};

const ChipInputRoot = styled(FormControl)(({ theme }) => ({
  root: {},
  inputRoot: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    flex: 1,
    marginTop: 0,
    minWidth: 70,
    '&.outlined, &.filled': {
      boxSizing: 'border-box',
    },
    '&.outlined': {
      paddingTop: 14,
    },
    '&.filled': {
      paddingTop: 28,
    },
  },
  input: {
    display: 'inline-block',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    appearance: 'none', // Remove border in Safari, doesn't seem to break anything in other browsers
    WebkitTapHighlightColor: 'rgba(0,0,0,0)', // Remove mobile color flashing (deprecated style).
    float: 'left',
    flex: 1,
  },
  chipContainer: {
    display: 'flex',
    flexFlow: 'row wrap',
    cursor: 'text',
    marginBottom: -2,
    minHeight: 40,
    '&.labeled&.standard': {
      marginTop: 18,
    },
  },
  outlined: {
    '& input': {
      height: 16,
      paddingTop: 4,
      paddingBottom: 12,
      marginTop: 4,
      marginBottom: 4,
    },
  },
  standard: {},
  filled: {
    '& input': {
      height: 22,
      marginBottom: 4,
      marginTop: 4,
      paddingTop: 0,
    },
    '$marginDense & input': {
      height: 26,
    },
  },
  labeled: {},
  label: {
    top: 4,
    '&.outlined&:not($labelShrink)': {
      top: 2,
      '$marginDense &': {
        top: 5,
      },
    },
    '&.filled&:not($labelShrink)': {
      top: 15,
      '$marginDense &': {
        top: 20,
      },
    },
  },
  labelShrink: {
    top: 0,
  },
  helperText: {
    marginBottom: -20,
  },
  focused: {},
  disabled: {},
  underline: {
    '&:after': {
      borderBottom: `2px solid ${theme.palette.primary[theme.palette.mode === 'light' ? 'dark' : 'light']}`,
      left: 0,
      bottom: 0,
      // Doing the other way around crash on IE 11 "''" https://github.com/cssinjs/jss/issues/242
      content: '""',
      position: 'absolute',
      right: 0,
      transform: 'scaleX(0)',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shorter,
        easing: theme.transitions.easing.easeOut,
      }),
      pointerEvents: 'none', // Transparent to the hover style.
    },
    '&.focused:after': {
      transform: 'scaleX(1)',
    },
    '&.error:after': {
      borderBottomColor: theme.palette.error.main,
      transform: 'scaleX(1)', // error is always underlined in red
    },
    '&:before': {
      borderBottom: `1px solid ${theme.palette.text.primary}`,
      left: 0,
      bottom: 0,
      // Doing the other way around crash on IE 11 "''" https://github.com/cssinjs/jss/issues/242
      content: '"\\00a0"',
      position: 'absolute',
      right: 0,
      transition: theme.transitions.create('border-bottom-color', {
        duration: theme.transitions.duration.shorter,
      }),
      pointerEvents: 'none', // Transparent to the hover style.
    },
    '&:hover:not($disabled):not($focused):not($error):before': {
      borderBottom: `2px solid ${theme.palette.text.primary}`,
      // Reset on touch devices, it doesn't add specificity
      '@media (hover: none)': {
        borderBottom: `1px solid ${theme.palette.text.primary}`,
      },
    },
    '&.disabled:before': {
      borderBottomStyle: 'dotted',
    },
  },
  error: {
    '&:after': {
      backgroundColor: theme.palette.error.main,
      transform: 'scaleX(1)', // error is always underlined in red
    },
  },
  chip: {
    margin: '0 8px 8px 0',
    float: 'left',
  },
  marginDense: {},
}));

const keyCodes = {
  BACKSPACE: 8,
  DELETE: 46,
  LEFT_ARROW: 37,
  RIGHT_ARROW: 39,
};

class ChipInput extends React.Component<Props> {
  static defaultProps: {
    allowDuplicates: boolean;
    blurBehavior: string;
    clearInputValueOnChange: boolean;
    disableUnderline: boolean;
    newChipKeyCodes: number[];
    newChipKeys: string[];
    variant: string;
  };
  state = {
    chips: [],
    errorText: undefined,
    focusedChip: null,
    inputValue: '',
    isClean: true,
    isFocused: false,
    chipsUpdated: false,
    prevPropsValue: [],
  };
  private labelRef: React.RefObject<HTMLLabelElement>;
  private input: React.RefObject<HTMLInputElement>;
  private labelNode: Element | Text | null;
  private inputBlurTimeout: number;
  private actualInput: React.Ref<HTMLInputElement>;
  private _keyPressed: boolean;
  private _preventChipCreation: boolean;

  constructor(props) {
    super(props);
    if (props.defaultValue) {
      this.state.chips = props.defaultValue;
    }
    this.labelRef = React.createRef();
    this.input = React.createRef();
  }

  static getDerivedStateFromProps(props, state): any {
    let newState: any = null;

    if (props.value && props.value.length !== state.prevPropsValue.length) {
      newState = { prevPropsValue: props.value };
      if (props.clearInputValueOnChange) {
        newState.inputValue = '';
      }
    }

    // if change detection is only needed for clearInputValueOnChange
    if (props.clearInputValueOnChange && props.value && props.value.length !== state.prevPropsValue.length) {
      newState = { prevPropsValue: props.value, inputValue: '' };
    }

    if (props.disabled) {
      newState = { ...newState, focusedChip: null };
    }

    if (!state.chipsUpdated && props.defaultValue) {
      newState = { ...newState, chips: props.defaultValue };
    }

    return newState;
  }

  componentDidMount(): void {
    if (this.props.variant === 'outlined') {
      this.labelNode = ReactDOM.findDOMNode(this.labelRef.current);
      this.forceUpdate();
    }
  }

  componentWillUnmount(): void {
    window.clearTimeout(this.inputBlurTimeout);
  }

  /**
   * Blurs this component.
   * @public
   */
  blur(): void {
    if (this.input) {
      // @ts-expect-error possibly null
      this.actualInput.blur();
    }
  }

  /**
   * Focuses this component.
   * @public
   */
  focus = (): void => {
    // @ts-expect-error possibly null
    this.actualInput.focus();
    if (this.state.focusedChip != null) {
      this.setState({ focusedChip: null });
    }
  };

  handleInputBlur = (event): void => {
    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
    this.setState({ isFocused: false });
    if (this.state.focusedChip != null) {
      this.setState({ focusedChip: null });
    }
    const value = event.target.value;
    let addChipOptions;
    switch (this.props.blurBehavior) {
      case 'add-or-clear':
        addChipOptions = { clearInputOnFail: true };
      // falls through
      case 'add':
        if (this.props.delayBeforeAdd) {
          // Lets assume that we only want to add the existing content as chip, when
          // another event has not added a chip within 200ms .
          // e.g. onSelection Callback in Autocomplete case
          const numChipsBefore = (this.props.value || this.state.chips).length;
          this.inputBlurTimeout = window.setTimeout(() => {
            const numChipsAfter = (this.props.value || this.state.chips).length;
            if (numChipsBefore === numChipsAfter) {
              this.handleAddChip(value, addChipOptions);
            } else {
              this.clearInput();
            }
          }, 150);
        } else {
          this.handleAddChip(value, addChipOptions);
        }
        break;
      case 'clear':
        this.clearInput();
        break;
    }
  };

  handleInputFocus = (event): void => {
    this.setState({ isFocused: true });
    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
  };

  handleKeyDown = (event): void => {
    const { focusedChip } = this.state;
    this._keyPressed = false;
    this._preventChipCreation = false;
    if (this.props.onKeyDown) {
      // Needed for arrow controls on menu in autocomplete scenario
      this.props.onKeyDown(event);
      // Check if the callback marked the event as isDefaultPrevented() and skip further actions
      // enter key for example should not always add the current value of the inputField
      if (event.isDefaultPrevented()) {
        return;
      }
    }
    const chips = this.props.value || this.state.chips;
    // @ts-expect-error default value
    if (this.props.newChipKeyCodes.indexOf(event.keyCode) >= 0 || this.props.newChipKeys.indexOf(event.key) >= 0) {
      const result = this.handleAddChip(event.target.value);
      if (result !== false) {
        event.preventDefault();
      }
      return;
    }

    switch (event.keyCode) {
      case keyCodes.BACKSPACE:
        if (event.target.value === '') {
          if (focusedChip != null) {
            this.handleDeleteChip(chips[focusedChip], focusedChip);
            if (focusedChip > 0) {
              this.setState({ focusedChip: focusedChip - 1 });
            }
          } else {
            this.setState({ focusedChip: chips.length - 1 });
          }
        }
        break;
      case keyCodes.DELETE:
        if (event.target.value === '' && focusedChip != null) {
          this.handleDeleteChip(chips[focusedChip], focusedChip);
          if (focusedChip <= chips.length - 1) {
            this.setState({ focusedChip });
          }
        }
        break;
      case keyCodes.LEFT_ARROW:
        if (focusedChip == null && event.target.value === '' && chips.length) {
          this.setState({ focusedChip: chips.length - 1 });
        } else if (focusedChip != null && focusedChip > 0) {
          this.setState({ focusedChip: focusedChip - 1 });
        }
        break;
      case keyCodes.RIGHT_ARROW:
        if (focusedChip != null && focusedChip < chips.length - 1) {
          this.setState({ focusedChip: focusedChip + 1 });
        } else {
          this.setState({ focusedChip: null });
        }
        break;
      default:
        this.setState({ focusedChip: null });
        break;
    }
  };

  handleKeyUp = (event): void => {
    if (
      !this._preventChipCreation &&
      ((this.props.newChipKeyCodes && this.props.newChipKeyCodes.indexOf(event.keyCode) >= 0) ||
        (this.props.newChipKeys && this.props.newChipKeys.indexOf(event.key) >= 0)) &&
      this._keyPressed
    ) {
      this.clearInput();
    } else {
      this.updateInput(event.target.value);
    }
    if (this.props.onKeyUp) {
      this.props.onKeyUp(event);
    }
  };

  handleKeyPress = (event): void => {
    this._keyPressed = true;
    if (this.props.onKeyPress) {
      this.props.onKeyPress(event);
    }
  };

  handleUpdateInput = (e): void => {
    if (this.props.inputValue == null) {
      this.updateInput(e.target.value);
    }

    if (this.props.onUpdateInput) {
      this.props.onUpdateInput(e);
    }
  };

  /**
   * Handles adding a chip.
   * @param {string|object} chip Value of the chip, either a string or an object (if dataSourceConfig is set)
   * @param {object=} options Additional options
   * @param {boolean=} options.clearInputOnFail If `true`, and `onBeforeAdd` returns `false`, clear the input
   * @returns True if the chip was added (or at least `onAdd` was called), false if adding the chip was prevented
   */
  handleAddChip(chip, options?: any): boolean {
    if (this.props.onBeforeAdd && !this.props.onBeforeAdd(chip)) {
      this._preventChipCreation = true;
      if (options != null && options.clearInputOnFail) {
        this.clearInput();
      }
      return false;
    }
    this.clearInput();
    const chips = this.props.value || this.state.chips;

    const { dataSourceConfig } = this.props;
    if (dataSourceConfig) {
      if (typeof chip === 'string') {
        chip = {
          [dataSourceConfig.text]: chip,
          [dataSourceConfig.value]: chip,
        };
      }

      if (this.props.allowDuplicates || !chips.some((c) => c[dataSourceConfig.value] === chip[dataSourceConfig.value])) {
        if (this.props.value && this.props.onAdd) {
          this.props.onAdd(chip);
        } else {
          this.updateChips([...this.state.chips, chip]);
        }
      }
      return true;
    }

    if (chip.trim().length > 0) {
      if (this.props.allowDuplicates || chips.indexOf(chip) === -1) {
        if (this.props.value && this.props.onAdd) {
          this.props.onAdd(chip);
        } else {
          this.updateChips([...this.state.chips, chip]);
        }
      }
      return true;
    }
    return false;
  }

  handleDeleteChip(chip, i): void {
    if (!this.props.value) {
      const chips = this.state.chips.slice();
      const changed = chips.splice(i, 1); // remove the chip at index i
      if (changed) {
        let focusedChip = this.state.focusedChip;
        if (this.state.focusedChip === i) {
          focusedChip = null;
          // @ts-expect-error possibly null
        } else if (this.state.focusedChip > i) {
          // @ts-expect-error possibly null
          focusedChip = this.state.focusedChip - 1;
        }
        this.updateChips(chips, { focusedChip });
      }
    } else if (this.props.onDelete) {
      this.props.onDelete(chip, i);
    }
  }

  updateChips(chips, additionalUpdates = {}): void {
    this.setState({ chips, chipsUpdated: true, ...additionalUpdates });
    if (this.props.onChange) {
      this.props.onChange(chips);
    }
  }

  /**
   * Clears the text field for adding new chips.
   * This only works in uncontrolled input mode, i.e. if the inputValue prop is not used.
   * @public
   */
  clearInput(): void {
    this.updateInput('');
  }

  updateInput(value): void {
    this.setState({ inputValue: value });
  }

  /**
   * Set the reference to the actual input, that is the input of the Input.
   * @param {object} ref - The reference
   */
  setActualInputRef = (ref: React.Ref<HTMLInputElement>): void => {
    this.actualInput = ref;
    if (this.props.inputRef) {
      this.props.inputRef(ref);
    }
  };

  render(): ReactElement {
    const {
      allowDuplicates,
      alwaysShowPlaceholder,
      blurBehavior,
      children,
      chipRenderer = defaultChipRenderer,
      classes,
      className,
      clearInputValueOnChange,
      dataSource,
      dataSourceConfig,
      defaultValue,
      delayBeforeAdd,
      disabled,
      disableUnderline,
      error,
      FormHelperTextProps,
      fullWidth,
      fullWidthInput,
      helperText,
      id,
      InputProps = {},
      inputRef,
      InputLabelProps = {},
      inputValue,
      label,
      newChipKeyCodes,
      newChipKeys,
      onBeforeAdd,
      onAdd,
      onBlur,
      onDelete,
      onChange,
      onFocus,
      onKeyDown,
      onKeyPress,
      onKeyUp,
      onUpdateInput,
      placeholder,
      readOnly,
      required,
      // @ts-expect-error prop not exist
      rootRef,
      value,
      variant,
      ...other
    } = this.props;

    const chips = value || this.state.chips;
    const actualInputValue = inputValue != null ? inputValue : this.state.inputValue;

    const hasInput = (this.props.value || actualInputValue).length > 0 || actualInputValue.length > 0;
    const shrinkFloatingLabel =
      InputLabelProps.shrink != null
        ? InputLabelProps.shrink
        : label != null && (hasInput || this.state.isFocused || chips.length > 0);

    const chipComponents = chips.map((chip, i) => {
      const value = dataSourceConfig ? chip[dataSourceConfig.value] : chip;
      return chipRenderer(
        {
          value,
          text: dataSourceConfig ? chip[dataSourceConfig.text] : chip,
          chip,
          isDisabled: !!disabled,
          isReadOnly: !!readOnly,
          isFocused: this.state.focusedChip === i,
          handleClick: () => this.setState({ focusedChip: i }),
          handleDelete: () => this.handleDeleteChip(chip, i),
          // @ts-expect-error possibly null
          className: classes.chip,
        },
        i,
      );
    });

    const InputMore: any = {};
    if (variant === 'outlined') {
      InputMore.notched = shrinkFloatingLabel;
      InputMore.labelWidth =
        // @ts-expect-error unknown field
        (shrinkFloatingLabel && this.labelNode && this.labelNode.offsetWidth) || 0;
    }

    if (variant !== 'standard') {
      InputMore.startAdornment = <React.Fragment>{chipComponents}</React.Fragment>;
    } else {
      // @ts-expect-error unknown value
      InputProps.disableUnderline = true;
    }

    // @ts-expect-error unknown value
    const InputComponent = variantComponent[variant];

    return (
      <ChipInputRoot
        ref={rootRef}
        fullWidth={fullWidth}
        className={className}
        error={error}
        required={chips.length > 0 ? undefined : required}
        onClick={this.focus}
        disabled={disabled}
        variant={variant}
        {...other}
      >
        {label && (
          <InputLabel
            htmlFor={id}
            // @ts-expect-error unknown value
            classes={{ root: cx(classes[variant], classes.label), shrink: classes.labelShrink }}
            shrink={shrinkFloatingLabel}
            focused={this.state.isFocused}
            variant={variant}
            ref={this.labelRef}
            required={required}
            {...InputLabelProps}
          >
            {label}
          </InputLabel>
        )}
        <div
          className={cx(
            // @ts-expect-error unknown value
            classes[variant],
            // @ts-expect-error unknown value
            classes.chipContainer,
            {
              // @ts-expect-error unknown value
              [classes.focused]: this.state.isFocused,
              // @ts-expect-error unknown value
              [classes.underline]: !disableUnderline && variant === 'standard',
              // @ts-expect-error unknown value
              [classes.disabled]: disabled,
              // @ts-expect-error unknown value
              [classes.labeled]: label != null,
              // @ts-expect-error unknown value
              [classes.error]: error,
            },
          )}
        >
          {variant === 'standard' && chipComponents}
          <InputComponent
            ref={this.input}
            classes={{
              // @ts-expect-error unknown value
              input: cx(classes.input, classes[variant]),
              // @ts-expect-error unknown value
              root: cx(classes.inputRoot, classes[variant]),
            }}
            id={id}
            value={actualInputValue}
            onChange={this.handleUpdateInput}
            onKeyDown={this.handleKeyDown}
            onKeyPress={this.handleKeyPress}
            onKeyUp={this.handleKeyUp}
            onFocus={this.handleInputFocus}
            onBlur={this.handleInputBlur}
            inputRef={this.setActualInputRef}
            disabled={disabled}
            fullWidth={fullWidthInput}
            placeholder={(!hasInput && (shrinkFloatingLabel || label == null)) || alwaysShowPlaceholder ? placeholder : null}
            readOnly={readOnly}
            {...InputProps}
            {...InputMore}
          />
        </div>
        {helperText && (
          <FormHelperText
            {...FormHelperTextProps}
            // @ts-expect-error unknown value
            className={FormHelperTextProps ? cx(FormHelperTextProps.className, classes.helperText) : classes.helperText}
          >
            {helperText}
          </FormHelperText>
        )}
      </ChipInputRoot>
    );
  }
}

ChipInput.defaultProps = {
  allowDuplicates: false,
  blurBehavior: 'clear',
  clearInputValueOnChange: false,
  disableUnderline: false,
  newChipKeyCodes: [13],
  newChipKeys: ['Enter'],
  variant: 'standard',
};

export default ChipInput;

export const defaultChipRenderer = (
  { value, text, isFocused, isDisabled, isReadOnly, handleClick, handleDelete, className },
  key,
): ReactElement => (
  <Chip
    key={key}
    className={className}
    style={{
      pointerEvents: isDisabled || isReadOnly ? 'none' : undefined,
      backgroundColor: isFocused ? blue[300] : undefined,
    }}
    onClick={handleClick}
    onDelete={handleDelete}
    label={text}
  />
);
